import React, { useEffect, useState } from "react";
import {
  Trophy,
  Crown,
  Sparkles,
  TrendingUp,
  RefreshCw,
  Coins,
  ShieldCheck,
  Layers,
  Medal,
  Flame,
  Lock,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { getAegisLeaderboard } from "../lib/api";
import LiveTicker from "../components/aegis/LiveTicker";

function formatProof(n) {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x >= 1_000_000) return `${(x / 1_000_000).toFixed(2)}M`;
  if (x >= 1_000) return `${(x / 1_000).toFixed(1)}k`;
  return Math.round(x).toLocaleString();
}

function formatApt(n) {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  if (x >= 1_000) return `${(x / 1_000).toFixed(2)}k`;
  return x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function RankBadge({ rank }) {
  if (rank === 1) return <Crown className="h-4 w-4 text-amber-300 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]" />;
  if (rank === 2) return <Medal className="h-4 w-4 text-zinc-300" />;
  if (rank === 3) return <Medal className="h-4 w-4 text-orange-400" />;
  return (
    <span className="font-mono text-[11px] text-zinc-500 tabular-nums w-4 text-center">
      {rank}
    </span>
  );
}

function LeaderRow({ row, isSelf, networkTotalApt, multiplier }) {
  const apt = Number(row.apt_balance ?? (row.tokens_saved || 0) / 1000);
  const share = networkTotalApt > 0 ? (apt / networkTotalApt) * 100 : 0;
  const mult = Number(multiplier || 1);
  const effectiveApt = Number(row.effective_apt ?? (apt * mult).toFixed(2));
  const hasUplift = mult > 1.0001;
  const isSystem = !!row.is_system;
  const tier = isSystem
    ? "border-sky-500/60 bg-sky-500/[0.08]"
    : row.rank === 1
    ? "border-amber-500/60 bg-amber-500/5"
    : row.rank <= 3
    ? "border-amber-500/30 bg-amber-500/[0.03]"
    : "border-zinc-800 bg-[#0b0b0d]";
  return (
    <article
      data-testid={`leaderboard-row-${row.rank}`}
      className={`relative border ${tier} ${
        isSelf ? "ring-1 ring-emerald-400/60" : ""
      } overflow-hidden`}
    >
      {/* Share-of-network progress bar (subtle, behind content) */}
      <div
        aria-hidden="true"
        className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-500/10 to-transparent pointer-events-none"
        style={{ width: `${Math.min(100, Math.max(2, share))}%` }}
      />
      <div className="relative p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
        <div className="shrink-0 w-8 flex items-center justify-center">
          <RankBadge rank={row.rank} />
        </div>

        <div className="shrink-0 h-10 w-10 border border-zinc-800 bg-black flex items-center justify-center overflow-hidden">
          {row.picture ? (
            <img src={row.picture} alt={row.handle} className="w-full h-full object-cover" />
          ) : (
            <img
              src="/brand/aegis-token-icon.png"
              alt=""
              className="w-full h-full object-cover opacity-70"
              style={{
                filter:
                  row.truth_count >= row.refuted_count
                    ? "saturate(1.05)"
                    : "hue-rotate(-20deg) saturate(1.15)",
              }}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div
              className="font-heading text-base tracking-[0.05em] text-zinc-100 truncate"
              data-testid={`leaderboard-handle-${row.rank}`}
            >
              {row.handle}
            </div>
            {row.is_architect && (
              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-amber-500/40 text-amber-300 bg-amber-500/5 inline-flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Architect
              </span>
            )}
            {isSystem && (
              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-sky-500/40 text-sky-300 bg-sky-500/5 inline-flex items-center gap-1">
                <Lock className="h-3 w-3" /> System · locked
              </span>
            )}
            {isSelf && (
              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.25em] border border-emerald-500/40 text-emerald-300 bg-emerald-500/5">
                you
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-mono">
            <span className="text-emerald-300/80">✓ {row.truth_count} truth</span>
            <span className="text-rose-300/80">✗ {row.refuted_count} refuted</span>
          </div>
        </div>

        <div className="shrink-0 text-right">
          <div
            className="font-mono text-3xl text-amber-300 leading-none drop-shadow-[0_0_10px_rgba(251,191,36,0.25)] tabular-nums"
            data-testid={`leaderboard-facts-${row.rank}`}
            title={`${row.facts_minted} verified truths surfaced by this contributor`}
          >
            {row.facts_minted}
          </div>
          <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-1 font-mono">
            △ facts
          </div>
        </div>
      </div>
    </article>
  );
}

export default function Leaderboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAegisLeaderboard(50)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  const rows = data?.leaderboard || [];
  const network = data?.network || {
    total_tokens_saved: 0,
    total_facts_minted: 0,
    total_tokens_burned: 0,
    circulating_proof: 0,
    burn_multiplier: 1.0,
    burn_count: 0,
  };

  return (
    <div className="min-h-[calc(100vh-130px)] bg-[#09090b] text-zinc-100">
      <LiveTicker fallbackMultiplier={network.burn_multiplier} />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-1 inline-flex items-center gap-2">
              <Trophy className="h-3 w-3" /> △ AEGIS · public leaderboard
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-[0.15em] text-zinc-100">
              Contributors
            </h1>
            <p className="text-zinc-400 text-sm mt-2 max-w-2xl leading-relaxed">
              Ranked by <span className="text-amber-300">verified truths surfaced</span>,
              not by wealth. This board honors the work of expanding the chain —
              every fact minted here made the network more trustworthy for
              everyone. Individual balances are private; only contribution
              counts appear on this leaderboard.
            </p>
          </div>
          <button
            onClick={() => setRefreshTick((t) => t + 1)}
            data-testid="leaderboard-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.2em] border border-zinc-800 text-zinc-400 hover:border-amber-500/60 hover:text-amber-300"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Network totals — saved · burned · circulating · multiplier */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
          <div
            className="border border-amber-500/40 bg-amber-500/5 p-4"
            data-testid="network-total-tokens"
            title={`${network.total_apt_minted?.toLocaleString?.() ?? "—"} AEGIS Points (APT) handed out · backed by ${network.total_tokens_saved.toLocaleString()} saved LLM tokens (PROOF)`}
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-1 flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> APT minted
            </div>
            <div className="font-mono text-3xl sm:text-4xl text-amber-300 leading-none drop-shadow-[0_0_14px_rgba(251,191,36,0.3)] tabular-nums">
              {formatProof(network.total_apt_minted || 0)}
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-2 font-mono">
              scarce token · 1 APT ≈ 1k saved tokens
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-600 mt-1 font-mono">
              backed by {formatProof(network.total_tokens_saved)} PROOF
            </div>
          </div>
          <div
            className="border border-rose-500/40 bg-rose-500/5 p-4"
            data-testid="network-total-burned"
            title="PROOF destroyed by paid memberships — 40% of every payment"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-rose-400 mb-1 flex items-center gap-1.5">
              <Flame className="h-3 w-3" /> Tokens burned
            </div>
            <div className="font-mono text-3xl sm:text-4xl text-rose-300 leading-none drop-shadow-[0_0_14px_rgba(244,63,94,0.3)] tabular-nums">
              {network.total_tokens_burned.toLocaleString()}
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-2 font-mono">
              {network.burn_count || 0} burn{network.burn_count === 1 ? "" : "s"} · 40% of every payment
            </div>
          </div>
          <div
            className="border border-zinc-800 bg-[#0b0b0d] p-4"
            data-testid="network-circulating"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1 flex items-center gap-1.5">
              <Coins className="h-3 w-3" /> Circulating
            </div>
            <div className="font-mono text-3xl sm:text-4xl text-zinc-200 leading-none tabular-nums">
              {network.circulating_proof.toLocaleString()}
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-2 font-mono">
              minted − burned
            </div>
          </div>
          <div
            className="border border-emerald-500/40 bg-emerald-500/5 p-4"
            data-testid="network-multiplier"
            title="Burn multiplier — every coin's implied value is uplifted by this factor"
          >
            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-1 flex items-center gap-1.5">
              <TrendingUp className="h-3 w-3" /> Multiplier
            </div>
            <div className="font-mono text-3xl sm:text-4xl text-emerald-300 leading-none drop-shadow-[0_0_14px_rgba(52,211,153,0.3)] tabular-nums">
              ×{Number(network.burn_multiplier || 1).toFixed(
                network.burn_multiplier < 10 ? 4 : 2
              )}
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-2 font-mono">
              applied to every coin
            </div>
          </div>
        </div>

        {/* Allocation breakdown — 51% treasury / 39% user / 5% architect / 5% burn */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-6" data-testid="allocation-strip">
          <div className="border border-sky-500/40 bg-sky-500/5 p-3" data-testid="alloc-vault">
            <div className="text-[10px] uppercase tracking-[0.3em] text-sky-400 mb-1 flex items-center gap-1.5">
              <Lock className="h-3 w-3" /> Treasury · 51% locked
            </div>
            <div className="font-mono text-xl text-sky-300 tabular-nums">
              {(network.liquidity_locked_proof || 0).toLocaleString()}
              <span className="text-zinc-500 text-xs"> PROOF</span>
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-1 font-mono">
              backs token value · never moves
            </div>
          </div>
          <div className="border border-amber-500/30 bg-amber-500/5 p-3" data-testid="alloc-treasury">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-1 flex items-center gap-1.5">
              <ShieldCheck className="h-3 w-3" /> Architect · 5%
            </div>
            <div className="font-mono text-xl text-amber-300 tabular-nums">
              {(network.architect_treasury_proof || 0).toLocaleString()}
              <span className="text-zinc-500 text-xs"> PROOF</span>
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-1 font-mono">
              funds ongoing development
            </div>
          </div>
          <div className="border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="alloc-float">
            <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 mb-1 flex items-center gap-1.5">
              <Coins className="h-3 w-3" /> User payout · 39%
            </div>
            <div className="font-mono text-xl text-emerald-300 tabular-nums">
              {(network.verifier_float_proof || 0).toLocaleString()}
              <span className="text-zinc-500 text-xs"> PROOF</span>
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-1 font-mono">
              flows to submitters · 5% burn on mint
            </div>
          </div>
        </div>

        {/* Leaderboard rows */}
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2">
            <Layers className="h-3 w-3" /> Ranked by tokens-saved · top {rows.length || 0}
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 font-mono inline-flex items-center gap-1">
            <TrendingUp className="h-3 w-3" /> live · refresh to update
          </div>
        </div>

        {loading && !data && (
          <div
            className="text-zinc-500 text-sm py-12 text-center font-mono uppercase tracking-[0.25em]"
            data-testid="leaderboard-loading"
          >
            Loading leaderboard…
          </div>
        )}
        {error && (
          <div
            className="text-rose-400 text-sm py-6 border border-rose-500/40 bg-rose-500/5 px-4 font-mono"
            data-testid="leaderboard-error"
          >
            {String(error)}
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div
            className="text-zinc-500 text-sm py-16 text-center border border-zinc-800 bg-[#0b0b0d]"
            data-testid="leaderboard-empty"
          >
            <div className="font-heading text-lg text-zinc-300 mb-2">
              No discoverers yet
            </div>
            <p className="text-xs uppercase tracking-[0.25em]">
              Verify a fact → seal a block → claim the first spot.
            </p>
          </div>
        )}
        {rows.length > 0 && (
          <div className="space-y-2" data-testid="leaderboard-list">
            {rows.map((row) => (
              <LeaderRow
                key={row.user_id || row.rank}
                row={row}
                isSelf={!!user?.user_id && row.user_id === user.user_id}
                networkTotalApt={network.total_apt_minted}
                multiplier={network.burn_multiplier}
              />
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-zinc-900 text-[10px] uppercase tracking-[0.3em] text-zinc-600 leading-loose">
          <div>{data?.notes}</div>
          <div className="mt-1">
            Public mint ledger · HMAC-signed · anyone can recompute these
            totals from{" "}
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/aegis/leaderboard`}
              target="_blank"
              rel="noreferrer noopener"
              className="text-amber-300/80 hover:text-amber-300 underline-offset-2 hover:underline"
              data-testid="leaderboard-raw-link"
            >
              the raw JSON
            </a>{" "}
            and the public chain.
          </div>
        </div>
      </div>
    </div>
  );
}
